// The world of one layout, scatter rule set and seed: the terrain, the chunks, the placements with their heights, each
// chunk's heightfield and scatter (pure, cached), its physics body and its nav geometry, the water courses, and the
// world hash. Everything here is a pure function of (layout, rules, seed, the game's collider lookup): two worlds built
// from the same inputs agree on every number, whichever chunks either built first.
//
// Since 0.2.0: placements' ground patches (a floor, a bridge's deck) answer `surfaceAt` over their footprint (IMP-0026;
// opt-in through `grounds`, and outside the hash, as the terrain's surface tags are), and `scatterStats` counts the
// scatter per region with each region's area (IMP-0028).
import type { BodySpec, ColliderSpec, Quat, Vec3 } from "@rpg/collision";
import { chunkGrid, type Area, type WorldChunk } from "./chunks";
import type { Layout, Placement, PlacementKind, ScatterRule } from "./layout";
import { shapeDistance } from "./geom";
import { scatterArea, WORLD_FAULTS, type ScatterContext, type ScatterItem } from "./scatter";
import { WorldHasher } from "./seeds";
import { createTerrain, type Terrain, type WaterCourse } from "./terrain";

/** A placement with its base height resolved. */
export interface PlacedPlacement extends Placement {
  /** The base height (m): the terrain under it, or the highest within its clearance (`level: "high"`). */
  y: number;
  yaw: number;
  seed: number;
  /** The chunk it belongs to (the one containing `pos`). */
  chunk: string;
  /** The radius kept clear of scatter. */
  clear: number;
}

/** A chunk's heightfield, as `@rpg/collision` and `@rpg/nav` take it: samples row-major from -z, centred on (cx, cz). */
export interface ChunkHeights {
  rows: number;
  cols: number;
  /** (rows + 1) × (cols + 1) heights (m). */
  heights: Float32Array;
  size: number;
  cx: number;
  cz: number;
}

/** A collider in world space (the nav geometry's and the overview's view of a chunk's solids). */
export interface WorldCollider {
  spec: ColliderSpec;
  position: Vec3;
  rotation: Quat;
}

/** Ground with its own surface tag over part of a placement (a house's floor, a bridge's deck; IMP-0026): a rectangle
 *  in the placement's own frame (its base at the origin, facing +z, as its colliders). */
export interface GroundPatch {
  /** The surface tag `surfaceAt` answers inside it ("wood", "stone"…). */
  surface: string;
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  /** When a query gives a height, the patch answers only at or above this height over the placement's base (m): a
   *  bridge's deck, not the stream bed under it. Default: at any height. */
  above?: number;
}

/** Scatter counted in one region (IMP-0028): its area and each rule's items standing in it. */
export interface RegionScatter {
  /** The region's id (the topmost region at each point, as `regionAt`), or null: outside every region. */
  region: string | null;
  /** Its area inside the counted rectangle (m²), sampled on the stats' grid. */
  area: number;
  /** Items per rule (every rule counted, zeros included). */
  counts: Record<string, number>;
  /** All the counted rules' items. */
  total: number;
  /** `total` per hectare (0 where the area is 0). */
  perHectare: number;
}

/** Scatter counts per region over a rectangle (`World.scatterStats`). */
export interface ScatterStats {
  /** The grid (m) the regions' areas were sampled on: one sample at each cell's centre. */
  step: number;
  /** The rectangle counted. */
  area: Area;
  /** The rules counted, in rule order. */
  rules: string[];
  /** The layout's regions in layout order, then outside every region (`region: null`). */
  regions: RegionScatter[];
  /** Every counted item in the rectangle. */
  total: number;
}

/** Inputs for a world. */
export interface WorldOptions {
  layout: Layout;
  rules?: readonly ScatterRule[];
  /** The world seed (default the layout's). */
  seed?: number;
  /** A placement's colliders, in its own frame (its base at the origin, facing +z); default none. The game asks its
   *  kit here (the kit seam: `kit.build(piece, seed, tier).colliders`). */
  colliders?: (p: Placement) => readonly ColliderSpec[];
  /** Placement kinds that get colliders from `colliders` (default building, prop, interactable, camp). Gatherables and
   *  breakables are the game's to make (their state lives in the sim). */
  solidKinds?: readonly PlacementKind[];
  /** A placement's ground patches (its floor, its deck), each with its surface tag; default none (`surfaceAt` reads
   *  the terrain alone, as before 0.2.0). Like `colliders`, the game asks its kit for the footprint. Asked once per
   *  placement, the first time `surfaceAt` runs. */
  grounds?: (p: PlacedPlacement) => readonly GroundPatch[];
  /** The collision layers the chunk bodies use (defaults "terrain" and "solid"). */
  layers?: { terrain: string; solid: string };
}

/** The world. */
export interface World {
  readonly layout: Layout;
  readonly rules: readonly ScatterRule[];
  readonly seed: number;
  readonly terrain: Terrain;
  /** Every chunk, row by row. */
  readonly chunks: readonly WorldChunk[];
  heightAt(x: number, z: number): number;
  /** The surface tag under (x, z): a placement's ground patch there (the first in layout order; given `y`, only a
   *  patch at or below it, by its `above`), else the terrain's (`terrain.surfaceAt`, which never sees patches: the
   *  ground's own colour under a floor stays the ground's). */
  surfaceAt(x: number, z: number, y?: number): string;
  /** The chunk containing (x, z), or null outside the bounds. */
  chunkAt(x: number, z: number): WorldChunk | null;
  chunk(id: string): WorldChunk;
  /** Placements of one kind (or all), in layout order, with their heights. */
  placements(kind?: PlacementKind): PlacedPlacement[];
  /** The placements in one chunk. */
  placementsIn(chunkId: string): PlacedPlacement[];
  /** A chunk's heightfield samples (cached). */
  heights(chunkId: string): ChunkHeights;
  /** A chunk's scatter (cached). */
  scatter(chunkId: string): readonly ScatterItem[];
  /** The scatter in any rectangle (built from its cells; not cached). */
  scatterIn(area: Area): ScatterItem[];
  /** Scatter counts per region (default over the whole bounds, every rule, on the terrain's cell): each region's area
   *  and the items of each rule standing in it, for a density check without a throwaway script. Not cached. */
  scatterStats(o?: { rules?: readonly string[]; step?: number; area?: Area }): ScatterStats;
  /** A chunk's physics: one fixed body ("chunk:<id>") with its heightfield, its trunks and big rocks, its placements'
   *  colliders. */
  body(chunkId: string): BodySpec;
  /** The solids in a chunk's area grown by `margin` m (scatter colliders and placements' colliders), in world space. */
  solidsNear(chunkId: string, margin: number): WorldCollider[];
  /** A heightfield over a chunk's area grown by `margin` m (a whole number of cells), for a nav tile's border. */
  heightsNear(chunkId: string, margin: number): ChunkHeights;
  water(): WaterCourse[];
  /** The world hash: terrain (every chunk's samples), scatter (every item), placements; and their combination. */
  hash(): { world: string; terrain: string; scatter: string; placements: string };
}

const DEFAULT_CLEAR: Record<PlacementKind, number> = { building: 7, prop: 1.5, gatherable: 0.8, breakable: 0.8, spawn: 2, camp: 4, interactable: 1.2, station: 1.5 };

/** Rotate (x, z) by `yaw` about +y (three.js's convention: yaw 0 faces +z). */
const rotY = (yaw: number, x: number, z: number): [number, number] => [x * Math.cos(yaw) + z * Math.sin(yaw), -x * Math.sin(yaw) + z * Math.cos(yaw)];
const yawQuat = (yaw: number): Quat => ({ x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) });
const mulQuat = (a: Quat, b: Quat): Quat => ({
  x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
  y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
  z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
  w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
});

/** Build a world. Cheap: chunks' contents are built (and cached) when first asked for. */
export function createWorld(o: WorldOptions): World {
  const layout = o.layout;
  const rules = o.rules ?? [];
  const seed = (o.seed ?? layout.seed) >>> 0;
  const terrain = createTerrain(layout, seed);
  const chunks = chunkGrid(layout);
  const byId = new Map(chunks.map((c) => [c.id, c]));
  const b = layout.bounds;
  const n = Math.round((b.x1 - b.x0) / layout.chunk);
  const layers = o.layers ?? { terrain: "terrain", solid: "solid" };
  const solidKinds = new Set<PlacementKind>(o.solidKinds ?? ["building", "prop", "interactable", "camp"]);

  const chunkAt = (x: number, z: number): WorldChunk | null => {
    if (x < b.x0 || x >= b.x1 || z < b.z0 || z >= b.z1) return null;
    return chunks[Math.floor((z - b.z0) / layout.chunk) * n + Math.floor((x - b.x0) / layout.chunk)] ?? null;
  };

  const placed: PlacedPlacement[] = layout.placements.map((p) => {
    const clear = p.clear ?? layout.clearance?.[p.kind] ?? DEFAULT_CLEAR[p.kind];
    let y = terrain.heightAt(p.pos[0], p.pos[1]);
    if (p.level === "high")
      for (let k = 0; k < 16; k++) {
        const a = (k / 16) * Math.PI * 2;
        for (const r of [clear * 0.5, clear]) y = Math.max(y, terrain.heightAt(p.pos[0] + Math.cos(a) * r, p.pos[1] + Math.sin(a) * r));
      }
    if (p.level === "span") {
      const yaw = p.yaw ?? 0;
      const dx = Math.sin(yaw) * clear;
      const dz = Math.cos(yaw) * clear;
      y = Math.max(terrain.heightAt(p.pos[0] + dx, p.pos[1] + dz), terrain.heightAt(p.pos[0] - dx, p.pos[1] - dz));
    }
    const c = chunkAt(p.pos[0], p.pos[1]);
    if (!c) throw new Error(`world: placement "${p.id}" is outside the bounds`);
    return { ...p, y, yaw: p.yaw ?? 0, seed: p.seed ?? 0, chunk: c.id, clear };
  });
  const ctx: ScatterContext = {
    seed,
    layout,
    terrain,
    placements: placed.map((p) => ({ p, clear: p.clear })),
    rules: new Map(rules.map((r) => [r.id, r])),
    // spacing's neighbours, built once per world (only rules with `spacing` fill it)
    memo: new Map(),
  };

  // ground patches (floors, decks), resolved to world space the first time `surfaceAt` runs
  type Patch = { g: GroundPatch; p: PlacedPlacement; r: number };
  let patches: Patch[] | null = null;
  const patchList = (): Patch[] => {
    if (patches) return patches;
    patches = [];
    if (o.grounds)
      for (const p of placed)
        for (const g of o.grounds(p)) patches.push({ g, p, r: Math.hypot(Math.max(Math.abs(g.x0), Math.abs(g.x1)), Math.max(Math.abs(g.z0), Math.abs(g.z1))) });
    return patches;
  };
  const surfaceAt = (x: number, z: number, y?: number): string => {
    // the planted defect sits here, beside what it breaks
    if (WORLD_FAULTS.groundsIgnored) return terrain.surfaceAt(x, z);
    for (const { g, p, r } of patchList()) {
      const dx = x - p.pos[0];
      const dz = z - p.pos[1];
      if (dx * dx + dz * dz > r * r) continue;
      const [lx, lz] = rotY(-p.yaw, dx, dz);
      if (lx < g.x0 || lx > g.x1 || lz < g.z0 || lz > g.z1) continue;
      if (y !== undefined && g.above !== undefined && y < p.y + g.above) continue;
      return g.surface;
    }
    return terrain.surfaceAt(x, z);
  };

  const heightsCache = new Map<string, ChunkHeights>();
  const scatterCache = new Map<string, readonly ScatterItem[]>();
  const colliderCache = new Map<string, readonly ColliderSpec[]>();

  const chunk = (id: string): WorldChunk => {
    const c = byId.get(id);
    if (!c) throw new Error(`world: no chunk "${id}"`);
    return c;
  };
  const sample = (x0: number, z0: number, size: number): ChunkHeights => {
    const cells = Math.round(size / layout.terrain.cell);
    const heights = new Float32Array((cells + 1) * (cells + 1));
    for (let iz = 0; iz <= cells; iz++) for (let ix = 0; ix <= cells; ix++) heights[iz * (cells + 1) + ix] = terrain.heightAt(x0 + ix * layout.terrain.cell, z0 + iz * layout.terrain.cell);
    return { rows: cells, cols: cells, heights, size, cx: x0 + size / 2, cz: z0 + size / 2 };
  };
  const heights = (id: string): ChunkHeights => {
    let h = heightsCache.get(id);
    if (!h) {
      const c = chunk(id);
      heightsCache.set(id, (h = sample(c.area.x0, c.area.z0, layout.chunk)));
    }
    return h;
  };
  const scatter = (id: string): readonly ScatterItem[] => {
    let s = scatterCache.get(id);
    if (!s) scatterCache.set(id, (s = scatterArea(ctx, rules, chunk(id).area)));
    return s;
  };
  const placementColliders = (p: PlacedPlacement): readonly ColliderSpec[] => {
    if (!o.colliders || !solidKinds.has(p.kind)) return [];
    let c = colliderCache.get(p.id);
    if (!c) colliderCache.set(p.id, (c = o.colliders(p)));
    return c;
  };
  const ruleOf = new Map(rules.map((r) => [r.id, r]));

  /** World-space solids of scatter items and placements. */
  const solidsOf = (items: readonly ScatterItem[], places: readonly PlacedPlacement[]): WorldCollider[] => {
    const out: WorldCollider[] = [];
    for (const it of items) {
      const r = ruleOf.get(it.rule);
      if (!r?.collider) continue;
      const k = it.scale;
      const spec: ColliderSpec =
        r.collider.shape === "cylinder"
          ? { shape: { type: "cylinder", halfHeight: r.collider.halfHeight * k, radius: r.collider.radius * k }, layer: r.layer ?? layers.solid, ...(r.tag ? { tag: r.tag } : {}) }
          : { shape: { type: "ball", radius: r.collider.radius * k }, layer: r.layer ?? layers.solid, ...(r.tag ? { tag: r.tag } : {}) };
      const up = r.collider.shape === "cylinder" ? r.collider.halfHeight * k : r.collider.radius * k * 0.5;
      out.push({ spec, position: { x: it.x, y: it.y + up, z: it.z }, rotation: { x: 0, y: 0, z: 0, w: 1 } });
    }
    for (const p of places) {
      const q = yawQuat(p.yaw);
      for (const c of placementColliders(p)) {
        const off = c.offset ?? { x: 0, y: 0, z: 0 };
        const [ox, oz] = rotY(p.yaw, off.x, off.z);
        const { offset: _o, rotation: _r, ...spec } = c;
        out.push({ spec, position: { x: p.pos[0] + ox, y: p.y + off.y, z: p.pos[1] + oz }, rotation: c.rotation ? mulQuat(q, c.rotation) : q });
      }
    }
    return out;
  };

  const grow = (a: Area, m: number): Area => ({ x0: a.x0 - m, x1: a.x1 + m, z0: a.z0 - m, z1: a.z1 + m });
  const inArea = (a: Area, x: number, z: number, r = 0): boolean => x + r >= a.x0 && x - r < a.x1 && z + r >= a.z0 && z - r < a.z1;

  let hashes: ReturnType<World["hash"]> | null = null;

  return {
    layout,
    rules,
    seed,
    terrain,
    chunks,
    heightAt: terrain.heightAt,
    surfaceAt,
    chunkAt,
    chunk,
    placements: (kind) => placed.filter((p) => !kind || p.kind === kind).map((p) => ({ ...p })),
    placementsIn: (id) => placed.filter((p) => p.chunk === id).map((p) => ({ ...p })),
    heights,
    scatter,
    scatterIn: (area) => scatterArea(ctx, rules, area),
    scatterStats(so = {}) {
      const area = so.area ?? { x0: b.x0, z0: b.z0, x1: b.x1, z1: b.z1 };
      const step = so.step ?? layout.terrain.cell;
      if (!(step > 0)) throw new Error(`world: scatterStats needs a step over 0 (got ${step})`);
      const counted = so.rules ? rules.filter((r) => so.rules!.includes(r.id)) : rules;
      for (const id of so.rules ?? []) if (!ruleOf.has(id)) throw new Error(`world: scatterStats: no rule "${id}"`);
      const ids = counted.map((r) => r.id);
      const rows = new Map<string | null, RegionScatter>();
      for (const id of [...layout.regions.map((r) => r.id), null]) rows.set(id, { region: id, area: 0, counts: Object.fromEntries(ids.map((k) => [k, 0])), total: 0, perHectare: 0 });
      // the topmost region at a point, as everything else reads it (the planted defect: the first one)
      const regionAt = WORLD_FAULTS.statsFirstRegion ? (x: number, z: number) => layout.regions.find((r) => shapeDistance(r.shape, x, z) <= 0)?.id ?? null : terrain.regionAt;
      for (let x = area.x0 + step / 2; x < area.x1; x += step) for (let z = area.z0 + step / 2; z < area.z1; z += step) rows.get(regionAt(x, z))!.area += step * step;
      let total = 0;
      for (const it of scatterArea(ctx, counted, area)) {
        const row = rows.get(regionAt(it.x, it.z))!;
        row.counts[it.rule]!++;
        row.total++;
        total++;
      }
      for (const row of rows.values()) row.perHectare = row.area > 0 ? row.total / (row.area / 10_000) : 0;
      return { step, area: { ...area }, rules: ids, regions: [...rows.values()], total };
    },
    body(id) {
      const c = chunk(id);
      const h = heights(id);
      const colliders: ColliderSpec[] = [{ shape: { type: "heightfield", rows: h.rows, cols: h.cols, heights: Array.from(h.heights), size: { x: h.size, y: 1, z: h.size } }, layer: layers.terrain, tag: layout.terrain.surface }];
      for (const s of solidsOf(scatter(id), placed.filter((p) => p.chunk === id)))
        colliders.push({ ...s.spec, offset: { x: s.position.x - c.cx, y: s.position.y, z: s.position.z - c.cz }, rotation: s.rotation });
      return { id: `chunk:${id}`, kind: "fixed", position: { x: c.cx, y: 0, z: c.cz }, colliders };
    },
    solidsNear(id, margin) {
      const a = grow(chunk(id).area, margin);
      return solidsOf(
        scatterArea(ctx, rules, a),
        placed.filter((p) => inArea(a, p.pos[0], p.pos[1], p.clear)),
      );
    },
    heightsNear(id, margin) {
      const c = chunk(id);
      const cells = Math.ceil(margin / layout.terrain.cell);
      const m = cells * layout.terrain.cell;
      return sample(c.area.x0 - m, c.area.z0 - m, layout.chunk + 2 * m);
    },
    water: () => terrain.water(),
    hash() {
      if (hashes) return hashes;
      const t = new WorldHasher();
      const s = new WorldHasher();
      const p = new WorldHasher();
      for (const c of chunks) {
        t.str(c.id);
        for (const v of heights(c.id).heights) t.num(v);
        s.str(c.id);
        for (const it of scatter(c.id)) s.str(it.rule).str(it.piece).num(it.x).num(it.y).num(it.z).num(it.yaw).num(it.scale).int(it.seed);
      }
      for (const q of placed) {
        p.str(q.id).str(q.kind).str(q.piece).num(q.pos[0]).num(q.pos[1]).num(q.y).num(q.yaw).int(q.seed);
        for (const c of placementColliders(q)) p.str(JSON.stringify(c));
      }
      const parts = { terrain: t.digest(), scatter: s.digest(), placements: p.digest() };
      hashes = { world: new WorldHasher().hex(parts.terrain).hex(parts.scatter).hex(parts.placements).digest(), ...parts };
      return hashes;
    },
  };
}
